import { useCallback, useState } from 'react';
import { sharedLoginCheck } from '../../authentication/sharedLoginCheck';
import { isEmailOnly } from 'dibs-cookie-jar/src/helpers/emailOnlyHelpers';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { refreshCheckout } from 'dibs-checkout/exports/prerender/initializePrerenderedCheckout';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
import { useRelayEnvironment, useFragment, graphql } from 'react-relay';
import { type useShippingAndReturns_user$key } from './__generated__/useShippingAndReturns_user.graphql';

const { EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const { INTERACTION_TYPE_SHIPPING_OPTIONS } = interactionTypeConstants;
const { STEP_INTERACTION_SHIPPING_SUGGESTION_CLICK } = stepInteractionConstants;

type OnSuccessfulPreQuoteRequestArgs = {
    userZipCode: string;
    userCountryCode: string;
};

interface UseShippingAndReturnsProps {
    user?: useShippingAndReturns_user$key | null;
    isSidebar?: boolean;
    onSuccessfulPreQuoteRequest?: (args: OnSuccessfulPreQuoteRequestArgs) => void;
    initialTab?: 'shipping' | 'returns';
}

export const useShippingAndReturns = ({
    user: userRef,
    isSidebar,
    onSuccessfulPreQuoteRequest,
    initialTab = 'shipping',
}: UseShippingAndReturnsProps): {
    modalOpen: boolean;
    showAuthModal: (options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
    }) => void;
    showCompleteAccount: () => void;
    openShippingModal: (status?: string) => void;
    closeShippingModal: () => void;
    onSuccessfulPreQuoteRequest: (args: OnSuccessfulPreQuoteRequestArgs) => void;
} => {
    const user = useFragment(
        graphql`
            fragment useShippingAndReturns_user on User {
                profile {
                    email
                }
            }
        `,
        userRef
    );
    const environment = useRelayEnvironment();

    const [modalOpen, setModalOpen] = useState(false);

    const showAuthModal = useCallback(
        ({
            onSuccess,
            onError,
        }: {
            onSuccess?: () => void;
            onError?: (error: unknown) => void;
        } = {}) => {
            sharedLoginCheck({
                onSuccess,
                onError,
                authOptions: {
                    flow: authModalLoader.constants.REQUEST_SHIPPING_FLOW,
                },
            });
        },
        []
    );

    const openShippingModal = useCallback(
        (status?: string) => {
            if (
                status === 'SHOW_ACCOUNT_COMPLETE' ||
                (isEmailOnly(document.cookie) && status === 'requestQuote')
            ) {
                const email = user?.profile?.email;
                authModalLoader
                    .show({
                        action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
                        flow: authModalLoader.constants.CREATE_PASSWORD_REQUEST_SHIPPING_MC_FLOW,
                        email,
                        relayEnvironment: environment,
                    })
                    .then(({ userId }: { userId?: string }) => {
                        if (userId) {
                            openShippingModal();
                        }
                    });
            } else {
                setModalOpen(true);

                if (initialTab === 'shipping') {
                    const label = 'request a shipping quote - pdp sidebar or details';
                    trackEvent(
                        {
                            isInteractiveEvent: true,
                            category: 'contact dealer',
                            action: 'contact dealer clicked',
                            label,
                            eventName: EVENT_PRODUCT_INTERACTION,
                            interaction_type: INTERACTION_TYPE_SHIPPING_OPTIONS,
                            step_interaction_name: STEP_INTERACTION_SHIPPING_SUGGESTION_CLICK,
                            trigger: isSidebar ? 'pdp main' : 'pdp details',
                        },
                        null
                    );
                }
            }
        },
        [isSidebar, user?.profile?.email, environment, initialTab]
    );

    const showCompleteAccount = useCallback(() => {
        openShippingModal('SHOW_ACCOUNT_COMPLETE');
    }, [openShippingModal]);

    const closeShippingModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const onSuccessfulPreQuoteRequestHandler = useCallback(
        (args: OnSuccessfulPreQuoteRequestArgs) => {
            onSuccessfulPreQuoteRequest?.(args);
            refreshCheckout();
        },
        [onSuccessfulPreQuoteRequest]
    );

    return {
        modalOpen,
        showAuthModal,
        showCompleteAccount,
        openShippingModal,
        closeShippingModal,
        onSuccessfulPreQuoteRequest: onSuccessfulPreQuoteRequestHandler,
    };
};
